import {
  DefaultRenderingPipeline,
  Effect,
  MotionBlurPostProcess,
  PostProcess,
  type AbstractMesh,
  type Camera,
  type DepthRenderer,
  type Observer,
  type Scene,
} from '@babylonjs/core';
import type { VisualSettings } from './Settings.js';

// --- Anime/cel post-process tuning. Bump OUTLINE_WIDTH for bolder lines; lower THRESHOLD to
//     catch fainter silhouettes; BANDS sets how many flat lighting steps; CEL_STRENGTH blends
//     the banding in (0 = off, 1 = full posterize). ---
const ANIME_OUTLINE_WIDTH = 1.6; // edge sample offset in texels (line thickness)
const ANIME_OUTLINE_THRESHOLD = 0.0065; // relative depth gap that counts as an edge
// Color-contrast edges catch flat features the depth pass misses — chiefly roads, which lie
// flush with the terrain so there's no silhouette. Higher = only strong contrasts (road vs
// grass) ink; lower also outlines subtler color changes.
const ANIME_COLOR_THRESHOLD = 0.16;
// The color-edge threshold ramps up with distance (depth d = viewZ/6000). Since roads contrast
// far harder with grass (~0.67) than paths do (~0.20), raising the bar with distance drops weak
// path outlines first and keeps strong road outlines longer — "roads before paths, close before
// far". Tune RAMP up to fade paths sooner.
const ANIME_COLOR_THRESHOLD_RAMP = 2.2;
// Road outlines are a close-range effect only: full strength up to FADE_START, gone by FADE_END
// (≈42 m → ≈96 m at maxZ 6000). Kept tight on purpose: on flat terrain everything from there to
// the horizon projects into a thin band near the skyline, so a wider fade stacked every distant
// road into a muddy cluster of black lines. Within the band the line ALSO thins with distance
// (see the shader's `oc` offset). Depth silhouettes (buildings/cars) are unaffected — they keep
// full width at any range.
const ANIME_COLOR_FADE_START = 0.007;
const ANIME_COLOR_FADE_END = 0.016;
const ANIME_BANDS = 4.0;
const ANIME_CEL_STRENGTH = 0.55;

export interface PostFxHandle {
  pipeline: DefaultRenderingPipeline;
  /** Attach/detach motion blur or just update its strength. No-op on WebGPU. */
  setMotionBlur(enabled: boolean, strength: number): void;
  /**
   * Whether motion blur is usable on the current backend. False on WebGPU — Babylon's
   * GLSL→WGSL translation of MotionBlurPostProcess currently emits invalid WGSL
   * (assignment to swizzle), so the post-process fails to compile.
   */
  motionBlurSupported: boolean;
  /**
   * Mark a mesh as never-blurred. Each frame, we force its stored previous-world matrix
   * to match its current world matrix, so the velocity buffer reads zero for that mesh
   * and the motion-blur shader skips it. Useful for the player vehicle (chase camera
   * follows it, so we want it to feel stationary in screen space).
   */
  excludeFromMotionBlur(mesh: AbstractMesh): void;
  /**
   * Update the radial speed-blur with the current forward speed and the user's intensity
   * slider. The post-process is created lazily on first non-zero intensity and disposed
   * when intensity drops back to zero, so there's no GPU cost when it's off.
   */
  updateSpeedBlur(forwardSpeed: number, intensity: number): void;
  /**
   * Toggle the anime/cel post-process (bold depth-silhouette ink outlines + posterized
   * lighting). Enabling it spins up a per-camera depth pass; disabling tears both down so
   * there's no GPU cost when off.
   */
  setAnimeStyle(enabled: boolean): void;
  dispose(): void;
}

/**
 * Minimal pipeline (MSAA only + small exposure/contrast nudge), plus an optional
 * camera-based MotionBlurPostProcess attached on demand.
 */
export function createPostFx(
  scene: Scene,
  camera: Camera,
  settings: VisualSettings,
  backend: 'webgpu' | 'webgl2',
): PostFxHandle {
  const pipeline = new DefaultRenderingPipeline('roadworld-postfx', true, scene, [camera]);

  // 4× MSAA only. FXAA stays off — it haloed distant edges.
  pipeline.samples = 4;
  pipeline.fxaaEnabled = false;
  pipeline.bloomEnabled = false;

  pipeline.imageProcessingEnabled = true;
  pipeline.imageProcessing.toneMappingEnabled = false;
  pipeline.imageProcessing.colorCurvesEnabled = false;
  pipeline.imageProcessing.vignetteEnabled = false;
  pipeline.imageProcessing.exposure = 1.0;
  pipeline.imageProcessing.contrast = 1.05;

  const motionBlurSupported = backend === 'webgl2';
  let mb: MotionBlurPostProcess | null = null;
  let warned = false;
  const excludedMeshes = new Set<AbstractMesh>();
  /**
   * Observer on the gbuffer's render target, not the scene-wide observable. The latter
   * fires before camera matrices are updated for the new frame, so any vp we'd write
   * would be stale by the time the gbuffer reads it. The render target's own observable
   * fires right before its render, after matrices are current.
   */
  let gbufObserver: Observer<number> | null = null;

  function attachExclusionHook(): void {
    if (gbufObserver) return;
    const gbuf = scene.geometryBufferRenderer;
    if (!gbuf) return;
    const rt = gbuf.getGBuffer();
    gbufObserver = rt.onBeforeRenderObservable.add(() => {
      const vp = scene.getTransformMatrix();
      for (const mesh of excludedMeshes) {
        const entry = gbuf._previousTransformationMatrices[mesh.uniqueId];
        if (!entry) continue;
        entry.world.copyFrom(mesh.getWorldMatrix());
        entry.viewProjection.copyFrom(vp);
      }
    });
  }

  function detachExclusionHook(): void {
    if (!gbufObserver) return;
    const gbuf = scene.geometryBufferRenderer;
    if (gbuf) gbuf.getGBuffer().onBeforeRenderObservable.remove(gbufObserver);
    gbufObserver = null;
  }

  function excludeFromMotionBlur(mesh: AbstractMesh): void {
    excludedMeshes.add(mesh);
    // If motion blur is already on, attach the hook now. Otherwise it'll be attached
    // when motion blur turns on.
    if (mb) attachExclusionHook();
  }

  // ----- Radial speed blur (custom screen-space post process) -----

  /** Forward speed (m/s) at which speed blur reaches full intensity. */
  const SPEED_BLUR_REF = 30;
  let radialBlur: PostProcess | null = null;
  let radialBlurAmount = 0; // updated per-frame, read by shader via onApply

  function updateSpeedBlur(forwardSpeed: number, intensity: number): void {
    const speedFactor = Math.min(1, Math.abs(forwardSpeed) / SPEED_BLUR_REF);
    const amount = intensity * speedFactor;
    radialBlurAmount = amount;

    if (amount > 0.001 && !radialBlur) {
      registerRadialBlurShader();
      radialBlur = new PostProcess(
        'roadworld-radial-blur',
        'roadworldRadialBlur',
        ['blurAmount'],
        null,
        1.0,
        camera,
      );
      radialBlur.onApply = (effect) => {
        effect.setFloat('blurAmount', radialBlurAmount);
      };
    } else if (amount <= 0.001 && radialBlur) {
      radialBlur.dispose();
      radialBlur = null;
    }
  }

  function setMotionBlur(enabled: boolean, strength: number): void {
    if (!motionBlurSupported) {
      if (enabled && !warned) {
        console.warn(
          '[PostFx] Motion blur disabled — Babylon\'s MotionBlurPostProcess emits invalid ' +
            'WGSL on WebGPU. Re-launch in a browser/build using WebGL2 to enable it.',
        );
        warned = true;
      }
      return;
    }
    if (enabled && !mb) {
      // Object-based motion blur: each mesh writes its world velocity into a velocity
      // buffer (we force the geometry buffer renderer via the last constructor arg).
      // Things moving with the camera (the car) get zero relative velocity → no blur,
      // which also fixes the white silhouette halo we saw with camera-based mode.
      mb = new MotionBlurPostProcess(
        'roadworld-motion-blur',
        scene,
        1.0,
        camera,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        /* forceGeometryBuffer */ true,
      );
      mb.isObjectBased = true;
      mb.motionStrength = strength;
      mb.motionBlurSamples = 16;
      attachExclusionHook();
    } else if (!enabled && mb) {
      detachExclusionHook();
      mb.dispose();
      mb = null;
    } else if (mb) {
      mb.motionStrength = strength;
    }
  }

  // ----- Anime/cel post process (depth-silhouette ink outlines + posterized lighting) -----

  let animePost: PostProcess | null = null;
  let depthRenderer: DepthRenderer | null = null;

  function setAnimeStyle(enabled: boolean): void {
    if (enabled && !animePost) {
      registerAnimeShader();
      // Per-camera linear-depth pass (viewZ/far in [0,1]); the shader reads it for both the
      // silhouette edge detect and to mask the sky (far plane) out of the cel banding.
      depthRenderer = scene.enableDepthRenderer(camera, false);
      animePost = new PostProcess(
        'roadworld-anime',
        'roadworldAnime',
        [
          'texelSize',
          'outlineWidth',
          'outlineThreshold',
          'colorThreshold',
          'colorThresholdRamp',
          'colorFadeStart',
          'colorFadeEnd',
          'bands',
          'celStrength',
        ],
        ['depthSampler'],
        1.0,
        camera,
      );
      animePost.onApply = (effect) => {
        const engine = camera.getEngine();
        effect.setFloat2('texelSize', 1 / engine.getRenderWidth(), 1 / engine.getRenderHeight());
        effect.setFloat('outlineWidth', ANIME_OUTLINE_WIDTH);
        effect.setFloat('outlineThreshold', ANIME_OUTLINE_THRESHOLD);
        effect.setFloat('colorThreshold', ANIME_COLOR_THRESHOLD);
        effect.setFloat('colorThresholdRamp', ANIME_COLOR_THRESHOLD_RAMP);
        effect.setFloat('colorFadeStart', ANIME_COLOR_FADE_START);
        effect.setFloat('colorFadeEnd', ANIME_COLOR_FADE_END);
        effect.setFloat('bands', ANIME_BANDS);
        effect.setFloat('celStrength', ANIME_CEL_STRENGTH);
        if (depthRenderer) effect.setTexture('depthSampler', depthRenderer.getDepthMap());
      };
    } else if (!enabled && animePost) {
      animePost.dispose();
      animePost = null;
      scene.disableDepthRenderer(camera);
      depthRenderer = null;
    }
  }

  // Honor initial settings state.
  setMotionBlur(settings.motionBlurEnabled, settings.motionBlurStrength);
  setAnimeStyle(settings.animeStyleEnabled);

  return {
    pipeline,
    setMotionBlur,
    motionBlurSupported,
    excludeFromMotionBlur,
    updateSpeedBlur,
    setAnimeStyle,
    dispose(): void {
      detachExclusionHook();
      excludedMeshes.clear();
      if (mb) {
        mb.dispose();
        mb = null;
      }
      if (radialBlur) {
        radialBlur.dispose();
        radialBlur = null;
      }
      if (animePost) {
        animePost.dispose();
        animePost = null;
        scene.disableDepthRenderer(camera);
        depthRenderer = null;
      }
      pipeline.dispose();
    },
  };
}

// --- Radial blur shader -----------------------------------------------------

let radialBlurShaderRegistered = false;

function registerRadialBlurShader(): void {
  if (radialBlurShaderRegistered) return;
  radialBlurShaderRegistered = true;

  // GLSL — Babylon auto-translates to WGSL for the WebGPU backend. No swizzle
  // assignments / no early `return` to keep the WGSL conversion happy.
  Effect.ShadersStore['roadworldRadialBlurFragmentShader'] = `
    precision highp float;
    varying vec2 vUV;
    uniform sampler2D textureSampler;
    uniform float blurAmount;

    void main(void) {
      vec2 center = vec2(0.5, 0.5);
      vec2 toCenter = vUV - center;
      float dist = length(toCenter);
      // No blur in the middle 15% — keeps the player area sharp; ramps up toward edges.
      float edgeWeight = smoothstep(0.15, 0.7, dist);
      float blur = blurAmount * edgeWeight;

      vec4 baseSample = texture2D(textureSampler, vUV);

      if (blur > 0.001) {
        vec3 sum = vec3(0.0);
        float totalWeight = 0.0;
        const int SAMPLES = 8;
        for (int i = 0; i < SAMPLES; i++) {
          float t = float(i) / float(SAMPLES - 1);
          // Sample inward toward center. Each pixel gets a streak of "where it came from"
          // as the camera (and world) rushed outward past it.
          vec2 offset = toCenter * blur * t * 0.08;
          float w = 1.0 - t * 0.5;
          sum += texture2D(textureSampler, vUV - offset).rgb * w;
          totalWeight += w;
        }
        baseSample = vec4(sum / totalWeight, baseSample.a);
      }

      gl_FragColor = baseSample;
    }
  `;
}

// --- Anime/cel shader -------------------------------------------------------

let animeShaderRegistered = false;

function registerAnimeShader(): void {
  if (animeShaderRegistered) return;
  animeShaderRegistered = true;

  // GLSL (auto-translated to WGSL for WebGPU). Two effects:
  //  1. Cel banding — quantize per-pixel luminance into `bands` steps and rescale RGB to that
  //     luminance (keeps hue, flattens shading). Masked off on the sky (far depth).
  //  2. Ink outline — sample linear depth at 4 neighbors; where the gap exceeds a
  //     depth-scaled threshold, darken toward black. This traces object silhouettes.
  // No swizzle assignments / no early return, so the WGSL conversion stays valid.
  Effect.ShadersStore['roadworldAnimeFragmentShader'] = `
    precision highp float;
    varying vec2 vUV;
    uniform sampler2D textureSampler;
    uniform sampler2D depthSampler;
    uniform vec2 texelSize;
    uniform float outlineWidth;
    uniform float outlineThreshold;
    uniform float colorThreshold;
    uniform float colorThresholdRamp;
    uniform float colorFadeStart;
    uniform float colorFadeEnd;
    uniform float bands;
    uniform float celStrength;

    void main(void) {
      vec4 col = texture2D(textureSampler, vUV);
      float d = texture2D(depthSampler, vUV).r;
      float notSky = step(d, 0.999);

      // Cel banding (luminance posterize, hue-preserving). The band EDGE is anti-aliased with
      // a screen-space derivative so smooth gradients (grass, terrain) don't show a hard contour
      // line at each step — flat-lit areas still read as crisp cel bands.
      float lum = dot(col.rgb, vec3(0.299, 0.587, 0.114));
      float lb = lum * bands;
      float aa = max(fwidth(lb), 1e-4);
      float q = (floor(lb) + smoothstep(0.5 - aa, 0.5 + aa, fract(lb))) / bands;
      float scale = lum > 0.001 ? q / lum : 1.0;
      vec3 celCol = col.rgb * scale;
      vec3 outRgb = mix(col.rgb, celCol, celStrength * notSky);

      vec2 o = texelSize * outlineWidth;

      // Depth-silhouette ink outline (object boundaries). Uses the SECOND derivative (Laplacian)
      // of depth, not the first difference: a first difference grows with surface slope, so every
      // grazing-angle hillside or curved ridge inked as soft, shadowy lines that faded in/out as
      // terrain tilted away. The Laplacian is ~0 on flat AND smoothly sloped/curved surfaces and
      // spikes only at true depth discontinuities (buildings, cars, sharp crests), so terrain
      // folds stop inking while object silhouettes stay.
      float dl = texture2D(depthSampler, vUV + vec2(-o.x, 0.0)).r;
      float dr = texture2D(depthSampler, vUV + vec2( o.x, 0.0)).r;
      float du = texture2D(depthSampler, vUV + vec2(0.0, -o.y)).r;
      float dn = texture2D(depthSampler, vUV + vec2(0.0,  o.y)).r;
      float ddH = abs(dl + dr - 2.0 * d);
      float ddV = abs(du + dn - 2.0 * d);
      float dEdge = max(ddH, ddV);
      float dt = outlineThreshold * (0.25 + d);
      float depthInk = smoothstep(dt, dt * 2.5, dEdge);

      // Color-contrast ink outline — catches flat features with no silhouette, chiefly roads
      // lying flush on the terrain. Uses full RGB distance so a dark road against green grass
      // reads as an edge while subtle grass mottling stays below threshold.
      // The color-tap offset shrinks with distance (full near, 0.35x by the fade end) so the
      // line thins as the road recedes and never grows wider than a thin, far-off road.
      vec2 oc = o * mix(1.0, 0.35, smoothstep(0.0, colorFadeEnd, d));
      vec3 cl = texture2D(textureSampler, vUV + vec2(-oc.x, 0.0)).rgb;
      vec3 cr = texture2D(textureSampler, vUV + vec2( oc.x, 0.0)).rgb;
      vec3 cu = texture2D(textureSampler, vUV + vec2(0.0, -oc.y)).rgb;
      vec3 cn = texture2D(textureSampler, vUV + vec2(0.0,  oc.y)).rgb;
      float cEdge = max(
        max(distance(col.rgb, cl), distance(col.rgb, cr)),
        max(distance(col.rgb, cu), distance(col.rgb, cn))
      );
      // Raise the bar with distance so weak edges (paths) drop out before strong ones (roads),
      // then cut everything off beyond the far band — close-and-strong outlines win.
      float ct = colorThreshold + d * colorThresholdRamp;
      float colorInk = smoothstep(ct, ct * 1.6, cEdge);
      colorInk *= 1.0 - smoothstep(colorFadeStart, colorFadeEnd, d);

      float ink = max(depthInk, colorInk) * notSky;
      outRgb = mix(outRgb, vec3(0.0), ink);

      gl_FragColor = vec4(outRgb, col.a);
    }
  `;
}
